import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import metadataQueryKeys from "shared/services/metadataQueryKeys";
import {
    CurrencyPreferences,
    CurrencyRule,
    DEFAULT_CURRENCY_CODE_PROPERTY,
    DEFAULT_NUMBER_FORMAT_PROPERTY,
} from "../services/currencyPreferences";

const usePlatformProperty = (name: string, enabled: boolean) => {
    return useQuery({
        queryKey: metadataQueryKeys.property(name),
        queryFn: () => new RecordManagement("sys_properties").where("name", name).first(),
        staleTime: 1000 * 60 * 5,
        retry: false,
        enabled,
    });
};

export const useCurrencyPreferences = (options?: { enabled?: boolean }) => {
    const enabled = options?.enabled ?? true;
    const currenciesQuery = useQuery({
        queryKey: metadataQueryKeys.currencies,
        queryFn: () => new RecordManagement("sys_currencies")
            .where("active", true)
            .paginate(1, 1000)
            .orderBy("code", "asc")
            .run(),
        staleTime: 1000 * 60 * 5,
        retry: false,
        enabled,
    });
    const defaultCurrencyCode = usePlatformProperty(DEFAULT_CURRENCY_CODE_PROPERTY, enabled);
    const defaultNumberFormat = usePlatformProperty(DEFAULT_NUMBER_FORMAT_PROPERTY, enabled);

    const preferences = useMemo<CurrencyPreferences>(() => {
        const currencies = currenciesQuery.data?.data.map((currency: any): CurrencyRule => ({
            id: currency.id,
            code: currency.code,
            symbol: currency.symbol,
            decimalPlaces: Number(currency.decimal_places ?? 0),
        })) ?? [];

        return {
            currencies,
            defaultCurrencyCode: (defaultCurrencyCode.data as any)?.value ?? null,
            defaultNumberFormat: (defaultNumberFormat.data as any)?.value ?? null,
        };
    }, [currenciesQuery.data?.data, defaultCurrencyCode.data, defaultNumberFormat.data]);

    return {
        data: preferences,
        isLoading: currenciesQuery.isLoading || defaultCurrencyCode.isLoading || defaultNumberFormat.isLoading,
        isError: currenciesQuery.isError || defaultCurrencyCode.isError || defaultNumberFormat.isError,
    };
};
